import {
  Action,
  createSelector,
  Selector,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import { Injectable } from '@angular/core';
import {
  BroadcastChannelService,
  MessageTypes,
} from '@shared/services/broadcast-channel.service';
import { AuthStateModel } from './auth.typings';
import {
  Logout,
  RefreshAuth,
  SetUserPersonalData,
  SignIn,
  SyncState,
} from './auth.actions';
import { Permission } from '../../types/permission';

const authStateBroadcastChannel = new BroadcastChannel('ngxs-sync');

const initialState = () => {
  return {
    accessToken: null,
    refreshToken: null,
    role: null,
    activeDivisionId: null,
    authorities: [],
    userPersonalData: null,
  };
};
@State<AuthStateModel>({
  name: 'auth',
  defaults: initialState(),
})
@Injectable()
export class AuthState {
  constructor(
    private readonly store: Store,
    private readonly broadcastChannel: BroadcastChannelService,
  ) {
    authStateBroadcastChannel.addEventListener(
      'message',
      (event: MessageEvent<AuthStateModel>) => {
        this.store.dispatch(new SyncState(event.data));
      },
    );
  }

  @Action(SignIn)
  signIn(ctx: StateContext<AuthStateModel>, action: SignIn) {
    ctx.patchState({
      ...action.auth,
    });
    authStateBroadcastChannel.postMessage(ctx.getState());
  }

  @Action(SetUserPersonalData)
  setUserPersonalData(
    ctx: StateContext<AuthStateModel>,
    action: SetUserPersonalData,
  ) {
    ctx.patchState({
      userPersonalData: action.userPersonalData,
    });
  }

  @Action(SyncState)
  syncState(ctx: StateContext<AuthStateModel>, action: SyncState) {
    ctx.setState(action.state);
  }

  @Action(RefreshAuth)
  refreshAuth(ctx: StateContext<AuthStateModel>, action: RefreshAuth) {
    const data = action.auth;
    ctx.patchState({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      role: data.role,
    });
    authStateBroadcastChannel.postMessage(ctx.getState());
  }

  @Action(Logout)
  logout(ctx: StateContext<AuthStateModel>) {
    ctx.patchState(initialState());
    this.broadcastChannel.postMessage({
      type: MessageTypes.NAVIGATE_LOGIN,
    });
  }

  @Selector()
  static getUserPersonalData(state: AuthStateModel) {
    return state.userPersonalData;
  }

  @Selector()
  static getAccessToken(state: AuthStateModel) {
    return state.accessToken;
  }

  @Selector()
  static getActiveDivisionId(state: AuthStateModel) {
    return state.activeDivisionId;
  }

  @Selector()
  static getRefreshToken(state: AuthStateModel) {
    return state.refreshToken;
  }

  @Selector()
  static isAuthenticated(state: AuthStateModel) {
    return !!state.accessToken;
  }

  static hasAuthority(authority: Permission) {
    return createSelector([AuthState], (state: AuthStateModel) => {
      return state.authorities.includes(authority);
    });
  }
}
